
import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "../styles/homeCampaignPopup.scss";

const STORAGE_KEY = "becker_home_campaign_popup_closed_at";
const POPUP_DELAY = 2000;
const HIDE_DURATION = 24 * 60 * 60 * 1000;

// 活動設定：未來修改活動只需要更改這裡
const CAMPAIGNS = [
  {
    id: "moving",
    tab: "喬遷優惠",
    label: "NEW BEGINNINGS",
    title: "喬遷新起點",
    subtitle: "全新環境，全新里程碑",
    description: "邀請你一起分享 Becker Taiwan 的喬遷喜悅，把握限定優惠。",
    image: "/homeImg/campaign-moving.png",
    fallbackImage: "/homeImg/campaign-moving.png",
    deadline: "2026-11-15T23:59:59+08:00",
    link: "https://beckertw.com/home/newdetail/693",
    buttonText: "查看喬遷優惠",
    enabled: true,
  },
  {
    id: "anniversary",
    tab: "週年慶",
    label: "ANNIVERSARY SPECIAL",
    title: "週年慶限定禮遇",
    subtitle: "專業成長，現在正是時候",
    description: "Becker Taiwan 週年慶活動，探索專屬優惠與進修機會。",
    image: "/homeImg/campaign-anniversary.png",
    fallbackImage: "/homeImg/campaign-anniversary.png",
    deadline: "2026-12-20T23:59:59+08:00",
    link: "",
    buttonText: "查看週年慶優惠",
    enabled: true,
  },
  {
    id: "cma",
    tab: "CMA 雙軌",
    label: "CMA EXAM NEWS",
    title: "中文 CMA 全新選擇",
    subtitle: "中文 CMA 台灣考場新消息",
    description: "了解 2027 年 11 月中文 CMA 台灣考場資訊與雙軌備考方案。",
    image: "/homeImg/campaign-cma.png",
    fallbackImage: "/homeImg/campaign-cma.png",
    deadline: null,
    link: "https://beckertw.com/home/newdetail/694",
    buttonText: "了解 CMA 雙軌方案",
    enabled: true,
  },
];

const getRemaining = (deadline, now) => {
  if (!deadline) return null;

  const diff = Math.max(0, new Date(deadline).getTime() - now);
  const seconds = Math.floor(diff / 1000);

  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
  };
};

const formatNumber = (number) => String(number).padStart(2, "0");

function CampaignImage({ campaign }) {
  const [src, setSrc] = useState(campaign.image);

  useEffect(() => {
    setSrc(campaign.image);
  }, [campaign.image]);

  return (
    <img
      src={src}
      alt={campaign.title}
      onError={() => {
        if (src !== campaign.fallbackImage) {
          setSrc(campaign.fallbackImage);
        }
      }}
    />
  );
}

export default function HomeCampaignPopup() {
  const [now, setNow] = useState(() => Date.now());
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // 每秒更新時間，倒數與到期下架共用
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const activeCampaigns = useMemo(() => {
    return CAMPAIGNS.filter((campaign) => {
      if (!campaign.enabled) return false;
      if (!campaign.deadline) return true;

      return new Date(campaign.deadline).getTime() > now;
    });
  }, [now]);

  const activeCampaign =
    activeCampaigns.find((campaign) => campaign.id === activeId) ||
    activeCampaigns[0];

  // 首次進入首頁時自動顯示
  useEffect(() => {
    if (activeCampaigns.length === 0) return;

    let shouldOpen = true;

    try {
      const lastClosed = Number(localStorage.getItem(STORAGE_KEY));

      if (
        lastClosed > 0 &&
        Date.now() - lastClosed < HIDE_DURATION
      ) {
        shouldOpen = false;
      }
    } catch (error) {
      console.warn("Popup storage unavailable:", error);
    }

    if (!shouldOpen) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, POPUP_DELAY);

    return () => clearTimeout(timer);
    // 只在初次掛載時檢查自動開啟
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 全部活動到期時自動關閉
  useEffect(() => {
    if (activeCampaigns.length === 0) {
      setIsOpen(false);
    }
  }, [activeCampaigns.length]);

  // 開啟時鎖住背景捲動、支援 ESC 關閉
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closePopup();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const closePopup = () => {
    setIsOpen(false);

    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (error) {
      console.warn("Unable to save popup preference:", error);
    }
  };

  const openPopup = () => {
    setActiveId(activeCampaigns[0]?.id || null);
    setIsOpen(true);
  };

  const countdown = activeCampaign
    ? getRemaining(activeCampaign.deadline, now)
    : null;

  if (activeCampaigns.length === 0) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && activeCampaign && (
          <motion.div
            className="becker-campaign-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closePopup();
              }
            }}
          >
            <motion.div
              className="becker-campaign-popup"
              role="dialog"
              aria-modal="true"
              aria-label="Becker Taiwan 最新活動"
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.3 }}
            >
              <button
                type="button"
                className="becker-campaign-close"
                onClick={closePopup}
                aria-label="關閉活動視窗"
              >
                ×
              </button>

              <div className="becker-campaign-header">
                <span>BECKER TAIWAN</span>
                <small>SPECIAL OFFERS 2026</small>
              </div>

              <div className="becker-campaign-tabs">
                {activeCampaigns.map((campaign) => (
                  <button
                    type="button"
                    key={campaign.id}
                    className={
                      activeCampaign.id === campaign.id ? "active" : ""
                    }
                    onClick={() => setActiveId(campaign.id)}
                  >
                    {campaign.tab}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCampaign.id}
                  className="becker-campaign-content"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="becker-campaign-image">
                    <CampaignImage campaign={activeCampaign} />
                  </div>

                  <div className="becker-campaign-info">
                    <div className="becker-campaign-label">
                      {activeCampaign.label}
                    </div>

                    <h2>{activeCampaign.title}</h2>
                    <h3>{activeCampaign.subtitle}</h3>
                    <p>{activeCampaign.description}</p>

                    {countdown ? (
                      <div className="becker-campaign-countdown">
                        <div className="becker-countdown-heading">
                          <span className="becker-countdown-dot" />
                          活動截止倒數
                        </div>

                        <div className="becker-countdown-grid">
                          {[
                            ["天", countdown.days],
                            ["時", countdown.hours],
                            ["分", countdown.minutes],
                            ["秒", countdown.seconds],
                          ].map(([label, value]) => (
                            <div className="becker-countdown-item" key={label}>
                              <strong>{formatNumber(value)}</strong>
                              <span>{label}</span>
                            </div>
                          ))}
                        </div>

                        <div className="becker-campaign-deadline">
                          截止日期：
                          {activeCampaign.id === "moving"
                            ? "2026/11/15"
                            : "2026/12/20"} 23:59
                        </div>
                      </div>
                    ) : (
                      <div className="becker-campaign-news">
                        <span className="becker-news-dot" />
                        最新消息已上架
                      </div>
                    )}

                    {activeCampaign.link ? (
                      <a
                        href={activeCampaign.link}
                        className="becker-campaign-cta"
                      >
                        {activeCampaign.buttonText}
                        <span aria-hidden="true">→</span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="becker-campaign-cta disabled"
                        disabled
                      >
                        活動頁面即將公布
                      </button>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isOpen && (
        <motion.button
          type="button"
          className="becker-campaign-floating"
          onClick={openPopup}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.04 }}
          aria-label="查看最新優惠活動"
        >
          <span className="becker-floating-icon">✦</span>
          <span>最新優惠</span>
          <span className="becker-floating-count">
            {activeCampaigns.length}
          </span>
        </motion.button>
      )}
    </>
  );
}
